import { useMemo } from 'react';

import { useUserPreferences } from '../stores/useUserPreferences';
import { isCurrencyStated } from './statedCurrency';

export interface ReportingContext {
  /** Beginning of the shared report period. */
  startDate: string;
  /** End of the shared report period; also the valuation date for snapshot reports. */
  endDate: string;
  /** Alias for `startDate`, kept explicit for callers that use WPF terminology. */
  periodStartDate: string;
  /** Alias for `endDate`, kept for existing snapshot/summary callers. */
  valuationDate: string;
  /** Trimmed code someone chose, or null when none is stated. */
  reportingCurrency: string | null;
  hasReportingCurrency: boolean;
  setStartDate: (isoDate: string) => void;
  setEndDate: (isoDate: string) => void;
  setPeriodStartDate: (isoDate: string) => void;
  setValuationDate: (isoDate: string) => void;
  setReportingCurrency: (currencyCode: string | null) => void;
}

/**
 * Canonical global inputs for LedgerVue snapshots and portfolio summary (parity with WPF valuation date + reporting CCY).
 * Prefer this hook over duplicating preference fields when wiring report API calls.
 */
export function useReportingContext(): ReportingContext {
  const periodStartDate = useUserPreferences((s) => s.periodStartDate);
  const valuationDate = useUserPreferences((s) => s.valuationDate);
  const defaultBookCurrency = useUserPreferences((s) => s.defaultBookCurrency);
  const setPeriodStartDate = useUserPreferences((s) => s.setPeriodStartDate);
  const setValuationDate = useUserPreferences((s) => s.setValuationDate);
  const setReportingCurrency = useUserPreferences((s) => s.setDefaultBookCurrency);

  const reportingCurrency = isCurrencyStated(defaultBookCurrency) ? defaultBookCurrency.trim() : null;
  const hasReportingCurrency = reportingCurrency !== null;
  const startDate = periodStartDate || '2025-01-01';
  const endDate = valuationDate;

  return useMemo(
    () => ({
      startDate,
      endDate,
      periodStartDate: startDate,
      valuationDate,
      reportingCurrency,
      hasReportingCurrency,
      setStartDate: setPeriodStartDate,
      setEndDate: setValuationDate,
      setPeriodStartDate,
      setValuationDate,
      setReportingCurrency,
    }),
    [
      startDate,
      endDate,
      valuationDate,
      reportingCurrency,
      hasReportingCurrency,
      setPeriodStartDate,
      setValuationDate,
      setReportingCurrency,
    ]
  );
}
